import React, { useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import HeightCoordinatorContext from './height-coordinator-context';

interface AnimatedFoldProps {
  isOpen: boolean;
  children: React.ReactNode;
}

const AnimatedFold: React.FC<AnimatedFoldProps> = ({ isOpen, children }) => {
  const { registerFold, requestHeightChange } = useContext(HeightCoordinatorContext);
  const id = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const unregisterRef = useRef<(() => void) | null>(null);
  const [renderChildren, setRenderChildren] = useState(isOpen);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  const setOuterRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (unregisterRef.current) {
        unregisterRef.current();
        unregisterRef.current = null;
      }
      if (el) {
        unregisterRef.current = registerFold(id, el, {
          onSettled: () => {
            if (!isOpenRef.current) setRenderChildren(false);
          }
        });
      }
    },
    [id, registerFold]
  );

  useEffect(() => {
    if (isOpen) setRenderChildren(true);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      requestHeightChange(id, 0);
      return;
    }
    if (!renderChildren) return;
    const el = contentRef.current;
    if (!el) return;
    requestHeightChange(id, el.getBoundingClientRect().height);
  }, [id, isOpen, renderChildren, requestHeightChange]);

  useEffect(() => {
    if (!renderChildren) return;
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (!isOpenRef.current) return;
      requestHeightChange(id, entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [id, renderChildren, requestHeightChange]);

  return (
    <div ref={setOuterRef} style={{ overflow: 'hidden' }}>
      <div ref={contentRef}>{renderChildren && children}</div>
    </div>
  );
};

export default AnimatedFold;
